import { Link } from "react-router-dom";
import btLogo from "@assets/bt-logo-white-mobile.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { RouteFactory } from "@src/app/routePaths";
import { sectionNavigationItems } from "@util/constant";
import cx from "classnames";

import {
  appDownloadLinks,
  footerFollowUsSocialLinks,
  footerLifestyleNavigationItems,
  footerLogos,
  footerSectionNavigationItems,
  footerSocialLinks,
  footerSocialLinksMobile,
  logoLinks,
  socialLinks,
} from "../constant";
import { LogoItem, NavigationItem, SocialLinkConfig } from "../types";

export function FooterLogos() {
  return (
    <div
      className={cx(
        "mb-6 mt-10 hidden items-center justify-between",
        "lg:flex"
      )}
    >
      {footerLogos.map((logo?: LogoItem) => {
        return logo ? (
          <Link
            to={logo.link}
            key={logo.link}
            target="_blank"
            rel="noreferrer"
            referrerPolicy="strict-origin-when-cross-origin"
            title={logo.alt}
          >
            <img src={logo.src} alt={logo.alt} />
          </Link>
        ) : null;
      })}
    </div>
  );
}

export function LifestyleLogo() {
  return logoLinks.bt ? (
    <div className="flex justify-center">
      <Link
        to={logoLinks.bt.link}
        key={logoLinks.bt.link}
        target="_blank"
        rel="noreferrer"
        referrerPolicy="strict-origin-when-cross-origin"
        title={logoLinks.bt.alt}
      >
        <img
          width={151}
          height={44}
          src={logoLinks.bt.src}
          alt={logoLinks.bt.alt}
        />
      </Link>
    </div>
  ) : null;
}

export function BreakingNewsOpinionAndFeatures() {
  return (
    <div className="mt-8 flex items-center font-poppins uppercase">
      {sectionNavigationItems["breaking-news"] ? (
        <Link
          className="mr-3 border-r pr-3"
          to={sectionNavigationItems["breaking-news"].link}
          reloadDocument
        >
          {sectionNavigationItems["breaking-news"].label}
        </Link>
      ) : null}
      {sectionNavigationItems["opinion-features"] ? (
        <Link
          to={sectionNavigationItems["opinion-features"].link}
          reloadDocument
        >
          {sectionNavigationItems["opinion-features"].label}
        </Link>
      ) : null}
    </div>
  );
}

export function EPaperLinks() {
  return (
    <div>
      <div
        className={cx(
          "mt-5 flex flex-col items-center font-poppins",
          "lg:items-start",
          "xl:flex-row xl:items-center"
        )}
      >
        {socialLinks.epaper ? (
          <div className="py-2 pr-3">
            <Link to={socialLinks.epaper.link} target="_blank" rel="noreferrer">
              <div className="flex">
                <FontAwesomeIcon icon={socialLinks.epaper.icon} size="xl" />
                <span className="ml-1">{socialLinks.epaper.label}</span>
              </div>
            </Link>
          </div>
        ) : null}
        {socialLinks.epaperBTArchive ? (
          <div className="flex justify-center rounded-full border px-4 py-2 text-center">
            <Link
              to={socialLinks.epaperBTArchive.link}
              target="_blank"
              rel="noreferrer"
            >
              {socialLinks.epaperBTArchive.label}
            </Link>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function FollowBT() {
  return (
    <div>
      <NavItemHeader title="Follow BT" />
      <div className="grid grid-cols-2">
        {footerSocialLinks.map((socialLink?: SocialLinkConfig) => {
          return socialLink ? (
            <Link
              className="inline-block py-3"
              to={socialLink.link}
              key={socialLink.link}
              target="_blank"
              rel="noreferrer"
              referrerPolicy="strict-origin-when-cross-origin"
              title={socialLink.alt}
            >
              <FontAwesomeIcon icon={socialLink.icon} size="xl" />
            </Link>
          ) : null;
        })}
      </div>
    </div>
  );
}

export function FollowBTMobile() {
  return (
    <div className="flex justify-between">
      {footerSocialLinksMobile.map((socialLink: SocialLinkConfig) => {
        return (
          <Link
            className="inline-block py-2"
            to={socialLink.link}
            key={socialLink.link}
            target="_blank"
            rel="noreferrer"
            referrerPolicy="strict-origin-when-cross-origin"
            title={socialLink.alt}
          >
            <FontAwesomeIcon icon={socialLink.icon} size="xl" />
          </Link>
        );
      })}
    </div>
  );
}

export function FollowUsBT() {
  return (
    <div className="lg:ml-[50%] lg:mt-5">
      <div className="fw-700 pb-3 font-lato text-xl text-gray-675">
        FOLLOW US
      </div>
      <div className="flex flex-wrap gap-x-4 lg:grid lg:grid-cols-2">
        {footerFollowUsSocialLinks.map((socialLink?: SocialLinkConfig) => {
          return socialLink ? (
            <Link
              className="inline-block pb-2"
              to={socialLink.link}
              key={socialLink.link}
              target="_blank"
              rel="noreferrer"
              referrerPolicy="strict-origin-when-cross-origin"
              title={socialLink.alt}
            >
              <FontAwesomeIcon icon={socialLink.icon} size="1x" />
            </Link>
          ) : null;
        })}
      </div>
    </div>
  );
}

function NavItem({ navItem }: { navItem: NavigationItem }) {
  return (
    <li key={navItem.link} className="py-2 font-poppins text-base">
      <Link to={navItem.link} reloadDocument>
        {navItem.label}
      </Link>
    </li>
  );
}

function NavItemHeader({ title }: { title: string }) {
  return (
    <h2 className="font-poppins text-base font-semibold uppercase text-gray-550">
      {title}
    </h2>
  );
}

function NavColumn({ navItems }: { navItems: NavigationItem[] }) {
  return (
    <>
      {navItems.map((navItem: NavigationItem) => {
        return <NavItem key={navItem.link} navItem={navItem} />;
      })}
    </>
  );
}

export function SectionsNavigationItems() {
  return (
    <div>
      <NavItemHeader title="SECTIONS" />
      <div className="flex">
        {footerSectionNavigationItems.map((column: NavigationItem[], index) => {
          return (
            <ul
              key={column[0]?.link}
              className={cx("w-1/2", index === 0 && "mr-6")}
            >
              <NavColumn navItems={column} />
            </ul>
          );
        })}
      </div>
    </div>
  );
}

export function LifestyleNavigationItems() {
  return (
    <div>
      <NavItemHeader title="LIFESTYLE" />
      <ul>
        <NavColumn navItems={footerLifestyleNavigationItems} />
      </ul>
    </div>
  );
}

export function FooterDownloadLinks() {
  return (
    <div className={cx("mt-6 flex justify-center px-3")}>
      <Link
        className={cx("mr-5 lg:mr-1")}
        to={appDownloadLinks.ios.link}
        target="_blank"
        rel=" noopener noreferrer"
      >
        <img
          width={135}
          height={40}
          src={appDownloadLinks.ios.src}
          alt={appDownloadLinks.ios.alt}
        />
      </Link>
      <Link
        to={appDownloadLinks.google.link}
        target="_blank"
        rel="noreferrer noopener"
      >
        <img
          width={135}
          height={40}
          src={appDownloadLinks.google.src}
          alt={appDownloadLinks.google.alt}
        />
      </Link>
    </div>
  );
}

export function FooterBTLogo() {
  return (
    <div className={cx("flex justify-center", "lg:ml-4 lg:justify-start")}>
      <Link to={RouteFactory.home} reloadDocument>
        <img width={370} height={38} src={btLogo} alt="The Business Times" />
      </Link>
    </div>
  );
}
